import * as ROUTES from '../common/routes';
import React from 'react';
import Tooltip from '@mui/material/Tooltip';
import clsx from 'clsx';
import { COLORS, FONTS, FONT_WEIGHT } from '../common/theme';
import { EIDs } from '../common/enums';
import { ReactComponent as IconStopWatch } from '../assets/svg/stopwatch.svg';
import { getText, getPath } from '../common/utils/helpers';
import { makeStyles, createStyles, StyleRules } from '@mui/styles';
import { useSelector, useDispatch } from 'react-redux';
import { useDynamicTranslations } from '../common/hooks/useTranslations';

const urlPrefix = getPath();

interface IProps {
  data: any;
  imageClassName?: any;
  uri?: string;
  onAdd?: (event: React.SyntheticEvent) => void;
  onView?: (event: React.SyntheticEvent) => void;
}

const useStyles = makeStyles(
  (theme): StyleRules =>
    createStyles({
      imageWrapperStyle: {
        position: 'relative',
        // maxWidth: '260px',
        width: '260px',
        height: '330px',
      },
      imgStyle: {
        width: '100%',
        height: '100%',
      },
      centerImageContainerStyle: {
        position: 'absolute',
        zIndex: 1,
        width: '190px',
        height: '120px',
        left: '36px',
        bottom: '90px',
        overflow: 'hidden',
      },
      centerImageStyle: {
        width: '100%',
        height: 'auto',
        objectFit: 'cover',
      },
      titleStyle: {
        position: 'absolute',
        left: '36px',
        top: '34px',
        width: '210px',
        fontWeight: FONT_WEIGHT.medium,
        fontSize: '22px',
        lineHeight: '26px',
        fontFamily: FONTS.garamond,
        color: COLORS.appBlue,
        // fontStyle: 'italic',
        zIndex: 10,
        boxSizing: 'border-box',
        paddingRight: '16px',
      },
      buttonsStyle: {
        position: 'absolute',
        width: '120px',
        bottom: '22px',
        right: '36px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        fontWeight: FONT_WEIGHT.medium,
        boxSizing: 'border-box',
      },
      buttonStyle: {
        width: '100%',
        height: '26px',
        background: COLORS.buttonGray,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        fontSize: '16px',
        fontFamily: FONTS.alegreya,
        color: COLORS.white,
        boxSizing: 'border-box',
        padding: '0 14px',
        cursor: 'pointer',
        '@media (hover: hover) and (pointer: fine)': {
          '&:hover': {
            opacity: 0.7,
            transition: '0.5s ease',
          },
        },
      },
      viewStyle: {
        width: '110px',
        marginBottom: '6px',
      },
      addStyle: {
        width: '160px',
        height: '26px',
        background: COLORS.buttonGray,
      },
      labelStyle: {
        paddingTop: '2px',
      },
      timerStyle: {
        position: 'relative',
        width: '24px',
        height: '24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      },
      watchStyle: {
        position: 'absolute',
        width: '24px',
        height: '24px',
        marginLeft: '6px',
      },
      timeStyle: {
        position: 'absolute',
        fontSize: '12px',
        fontFamily: FONTS.alegreya,
        color: COLORS.white,
        padding: '4px 0 0 8px',
      },
      viewCountStyle: {
        width: '22px',
        height: '22px',
        fontSize: '14px',
        fontFamily: FONTS.alegreya,
        color: COLORS.white,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: `1px solid ${COLORS.white}`,
        borderRadius: '50%',
        marginLeft: '4px',
      },
    })
);

const SurveyImage = ({
  data,
  imageClassName,
  onAdd,
  onView,
}: IProps): JSX.Element => {
  const classes = useStyles();
  const lng = useSelector((state: any) => state.app.lng);
  const appData = useSelector((state: any) => state.app.appData);
  const themeData = appData.find((item) => item.content.uid === EIDs.THEMES);
  const imagesData = themeData.images;

  const lngType = data?.content?.type || lng;
  const readLabel = useDynamicTranslations('readstory', lngType);
  const addLabel = useDynamicTranslations('addstory', lngType);

  const viewSingleTooltip = useDynamicTranslations(
    'viewsingletooltip',
    lngType
  );
  const viewMultipleTooltip = useDynamicTranslations(
    'viewmultipletooltip',
    lngType
  );
  const addTooltip = useDynamicTranslations('addtimetooltip', lngType);

  const randomNr = Math.floor(Math.random() * imagesData.length);
  const url = imagesData[randomNr].url;
  const imgUrl: string = `${urlPrefix}${url}`;

  const centerImg = data?.images?.[0].url;
  const centerImageUrl: string = `${urlPrefix}${centerImg}`;

  const getTime = () => {
    const time = data.content.time;
    const [hours, minutes, seconds] = time.split(':');
    const visibleTime =
      parseInt(hours) * 60 + parseInt(minutes) + parseInt(seconds) / 60;

    return visibleTime;
  };

  const getViewIcon = () => {
    const totalSurveys = data.content.totalSurveys || 0;
    const amountLabel =
      totalSurveys === 1 ? viewSingleTooltip : viewMultipleTooltip;
    const label = amountLabel.replace('{x}', totalSurveys);

    return (
      <Tooltip title={label} placement={'top-end'} arrow>
        <span className={classes.viewCountStyle}>{totalSurveys}</span>
      </Tooltip>
    );
  };

  const getAddIcon = () => {
    const addTime = getTime();
    const label = addTooltip.replace('{x}', addTime.toString());

    return (
      <Tooltip title={label} placement={'bottom-end'} arrow>
        <div className={classes.timerStyle}>
          <IconStopWatch className={classes.watchStyle} />
          <span className={classes.timeStyle}>{addTime}</span>
        </div>
      </Tooltip>
    );
  };

  const rawSurveyName: string = getText(data?.content, 'title', lng);
  const surveyName = rawSurveyName.replace(/(?:\r\n|\r|\n)/g, '<br>');

  return (
    <div className={clsx(classes.imageWrapperStyle, imageClassName)}>
      <div
        className={classes.titleStyle}
        dangerouslySetInnerHTML={{ __html: surveyName }}
      />
      <img src={imgUrl} alt="" className={classes.imgStyle} />
      {centerImg && (
        <div className={classes.centerImageContainerStyle}>
          <img
            src={centerImageUrl}
            alt=""
            className={classes.centerImageStyle}
          />
        </div>
      )}
      {Boolean(onAdd) && (
        <div className={classes.buttonsStyle}>
          <div
            onClick={onView}
            className={clsx(classes.buttonStyle, classes.viewStyle)}
            data-uri={data.content.uri}
          >
            <span className={classes.labelStyle}>{readLabel}</span>
            {getViewIcon()}
          </div>
          <div
            onClick={onAdd}
            className={clsx(classes.buttonStyle, classes.addStyle)}
          >
            <span className={classes.labelStyle}>{addLabel}</span>
            {getAddIcon()}
          </div>
        </div>
      )}
    </div>
  );
};

export default SurveyImage;
